import Heading from "@afa-shared/afa-components/dist/Heading";
import Icon from "@afa-shared/afa-components/dist/Icon";
import Tag from "@afa-shared/afa-components/dist/Tag";
import Text from "@afa-shared/afa-components/dist/Text";
import clsx from "clsx";
import { useState } from "react";

import { EducationCourseType, EducationProviderItem } from "types/EducationProviderItem";

import educationAccordionStyles from "./EducationAccordion.module.css";

interface IEducationAccordion {
  educationProviders: Array<EducationProviderItem>;
}

const EducationAccordions = ({ educationProviders }: IEducationAccordion) => {
  return (
    <ul className={educationAccordionStyles.educationAccordion}>
      {educationProviders?.map((provider: EducationProviderItem) => (
        <EducationAccordion key={provider.organizationalNumber} {...provider} />
      ))}
    </ul>
  );
};

export default EducationAccordions;

function EducationAccordion({
  name,
  municipality,
  courseTypes,
  organizationalNumber,
  address,
  email,
  zip,
  contact,
  phone,
}) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const labelId = makeDomId(organizationalNumber, "label");
  return (
    <li
      className={clsx(educationAccordionStyles.accordionItem, {
        [educationAccordionStyles.active]: isOpen,
      })}
    >
      <button
        className={educationAccordionStyles.headerButton}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-labelledby={labelId}
      >
        <div className={educationAccordionStyles.headerTop}>
          <div className={educationAccordionStyles.headerTopLeft}>
            <Heading className={educationAccordionStyles.heading} variant="h3" id={labelId}>
              {name}
            </Heading>
            <div>
              <Tag
                className={educationAccordionStyles.tag}
                label={municipality?.toLowerCase()}
                color={"dark_blue"}
              />
            </div>
          </div>
          <Icon name={isOpen ? "remove" : "add"} size="sm" referenceSize="h3" />
        </div>

        {courseTypes && (
          <ul className={educationAccordionStyles.tagList}>
            {courseTypes.map((tag: EducationCourseType, i) => {
              return (
                <li key={i}>
                  <Tag className={educationAccordionStyles.tag} label={tag.name} />
                </li>
              );
            })}
          </ul>
        )}
      </button>
      <div
        className={clsx(educationAccordionStyles.body, {
          [educationAccordionStyles.active]: isOpen,
        })}
      >
        <div>
          {organizationalNumber && (
            <div className={educationAccordionStyles.contactWrapper}>
              <Icon name="organization_number_square" size="sm" referenceSize="span" />{" "}
              <Text variant="span">{organizationalNumber}</Text>
            </div>
          )}
          {address && (
            <div className={educationAccordionStyles.contactWrapper}>
              <Icon name="map_square" size="sm" referenceSize="span" />
              <address className={educationAccordionStyles.address}>
                <Text variant="span">{address?.toLowerCase()}</Text>
                <Text variant="span">
                  {zip} {municipality?.toLowerCase()}
                </Text>
              </address>
            </div>
          )}
        </div>
        <div>
          {name && (
            <div className={educationAccordionStyles.contactWrapper}>
              <Icon name="contact_person_square" size="sm" referenceSize="span" />
              <Text variant="span">{contact}</Text>
            </div>
          )}
          {email && (
            <div className={educationAccordionStyles.contactWrapper}>
              <Icon name="email" size="sm" referenceSize="span" />
              <Text variant="link">
                <a href={`mailto:${email}`}>{email}</a>
              </Text>
            </div>
          )}
          {phone && (
            <div className={educationAccordionStyles.contactWrapper}>
              <Icon name="phone_square" size="sm" referenceSize="span" />
              <Text variant="link">
                <a href={`tel:${phone}`}>{phone}</a>
              </Text>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function makeDomId(org: string, suffix: string) {
  const base = `${org}-${suffix}`
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9_-]+/g, "-");

  return `edu-${base}`;
}
